import { RoomEvent, Track } from 'livekit-client';
import type { Room, RemoteTrack, RemoteParticipant, RemoteTrackPublication } from 'livekit-client';
import type { EventBus } from '../event-bus';
import type { RoomManager } from '../room';

export interface AudioBuiltinOptions {
  roomManager: RoomManager;
  eventBus: EventBus;
}

export interface AudioBuiltinHandle {
  /**
   * Mute/unmute every currently-attached (and future) agent `<audio>`
   * element. LiveKit's subscribed remote tracks play through the page's
   * default audio output with no built-in "output volume" concept, so the
   * `toggleVolume` control in bootstrap.ts has to reach into the actual
   * elements this built-in attaches rather than just flipping a display flag.
   */
  setOutputMuted(muted: boolean): void;
}

/**
 * Core audio playback built-in (non-avatar path).
 *
 * The embed's `room.ts` wires transcription/data/state events but does NOT
 * attach any remote audio, so on its own the agent speaks into the void — you
 * see the transcript but hear nothing. The avatar built-in attaches agent
 * audio, but only when an `avatar` config is present.
 *
 * This built-in fills that gap: when avatar mode is OFF, it subscribes to the
 * Room and attaches every remote audio track to a hidden `<audio autoplay>`
 * element so the agent's voice actually plays. Mirrors the unconditional
 * attach the native overlay does in `voice-session-store.ts`.
 *
 * Installed by bootstrap ONLY in the `else` of the avatar branch, so agent
 * audio is never double-attached (avatar mode already handles it, plus its
 * mute-routing when the avatar voices the agent).
 */
export function installAudioBuiltin(opts: AudioBuiltinOptions): AudioBuiltinHandle {
  const { roomManager, eventBus } = opts;
  const audioElements = new Map<RemoteTrack, HTMLAudioElement>();
  let attachedRoom: Room | null = null;
  let outputMuted = false;

  function handleTrackSubscribed(
    track: RemoteTrack,
    _publication: RemoteTrackPublication,
    _participant: RemoteParticipant,
  ): void {
    if (track.kind !== Track.Kind.Audio) return;
    let audioEl: HTMLAudioElement | null = null;
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      audioEl = (track as any).attach() as HTMLAudioElement;
    } catch (e) {
      console.warn('[Mobeus] agent audio track.attach threw', e);
      return;
    }
    if (!audioEl) return;
    audioEl.autoplay = true;
    audioEl.muted = outputMuted;
    audioEl.setAttribute('data-mobeus-agent-audio', '');
    document.body.appendChild(audioEl);
    audioElements.set(track, audioEl);
  }

  function handleTrackUnsubscribed(track: RemoteTrack): void {
    const el = audioElements.get(track);
    if (!el) return;
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (track as any).detach(el);
    } catch (e) {
      console.warn('[Mobeus] agent audio track.detach threw', e);
    }
    el.remove();
    audioElements.delete(track);
  }

  function teardown(): void {
    for (const [track, el] of audioElements) {
      try {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (track as any).detach(el);
      } catch {
        /* ignore */
      }
      el.remove();
    }
    audioElements.clear();
  }

  eventBus.on('sessionStart', () => {
    const room = roomManager.getRoom();
    if (!room || attachedRoom === room) return;
    attachedRoom = room;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (room as any).on(RoomEvent.TrackSubscribed, handleTrackSubscribed);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (room as any).on(RoomEvent.TrackUnsubscribed, handleTrackUnsubscribed);
  });

  eventBus.on('sessionEnd', () => {
    teardown();
    attachedRoom = null;
  });

  return {
    setOutputMuted(muted) {
      outputMuted = muted;
      for (const el of audioElements.values()) el.muted = muted;
    },
  };
}
